import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Ic_delete_green from "../../../assets/images/Ic_delete_green.svg";
import Img_pdf from "../../../assets/images/Img_pdf.png";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../../../components/ui/form";
import Button from "../../../components/common/button";
import { Input } from "../../../components/ui/input";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { db, storage } from "../../../config/firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { Spinner } from "../../../components/Spinner";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { Upload } from "lucide-react";

const formSchema = z.object({
  navn: z.string().min(1, "Navn er påkrevd"),
  pris: z.string().min(1, "Signert av kunde (dato) er påkrevd"),
  date: z.string().min(1, "Dato er påkrevd"),
  pdf: z
    .array(
      z.union([
        z
          .instanceof(File)
          .refine(
            (file: any) => file === null || file.size <= 10 * 1024 * 1024,
            {
              message: "Filstørrelsen må være mindre enn 10 MB.",
            }
          ),
        z.string(),
      ])
    )
    .min(1, "Minst ett bilde kreves."),
});

export const Endringsmelding: React.FC<{
  setEndringsmelding: any;
  setEndringsmeldingData: any;
  EndringsmeldingData: any;
}> = ({ setEndringsmelding, setEndringsmeldingData, EndringsmeldingData }) => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const id = pathSegments.length > 2 ? pathSegments[2] : null;
  const [loading, setLoading] = useState(false);

  const form = useForm<any>({
    resolver: zodResolver(formSchema),
  });
  const filepdfInputRef = React.useRef<HTMLInputElement | null>(null);
  const uploadpdf: any = form.watch("pdf");

  const handleFileUpload = async (files: FileList, fieldName: any) => {
    if (!files.length) return;

    const currentImages = Array.isArray(uploadpdf) ? uploadpdf : [];
    let newImages = [...currentImages];

    const uploadPromises = Array.from(files).map(async (file) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB.", {
          position: "top-right",
        });
        return null;
      }

      const fileType = "documents";
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${fileType}/${fileName}`);

      try {
        const snapshot = await uploadBytes(storageRef, file);
        return await getDownloadURL(snapshot.ref);
      } catch (error) {
        console.error("Error uploading file:", error);
        return null;
      }
    });

    const uploadedUrls = (await Promise.all(uploadPromises)).filter(
      Boolean
    ) as string[];

    if (uploadedUrls.length) {
      newImages = [...newImages, ...uploadedUrls];
      form.setValue(fieldName, newImages);
      form.clearErrors(fieldName);
    }
  };

  const handlepdfFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files) {
      await handleFileUpload(event.target.files, "pdf");
    }
  };

  const handlepdfClick = () => {
    filepdfInputRef.current?.click();
  };

  const handlepdfDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      await handleFileUpload(event.dataTransfer.files, "pdf");
    }
  };

  const handlepdfDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setEndringsmeldingData(data);

      const formatDate = (date: Date) => {
        return date
          .toLocaleString("sv-SE", { timeZone: "UTC" })
          .replace(",", "");
      };

      const docRef = doc(db, "bank_leads", String(id));

      const existingData = (await getDoc(docRef)).data()?.Fremdriftsplan || {};
      await updateDoc(docRef, {
        Fremdriftsplan: { ...existingData, Endringsmelding: data },
        updatedAt: formatDate(new Date()),
      });
      toast.success("Lagret", { position: "top-right" });
      setEndringsmelding(false);
    } catch (error) {
      console.error("Firestore operation failed:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (EndringsmeldingData) {
      form.setValue("pris", EndringsmeldingData?.pris);
      form.setValue("date", EndringsmeldingData?.date);
      form.setValue("pdf", EndringsmeldingData?.pdf);
      form.setValue("navn", EndringsmeldingData?.navn);
    }
  }, []);

  return (
    <>
      {loading && <Spinner />}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
          <div className="p-5 flex flex-col gap-5">
            <FormField
              control={form.control}
              name={`navn`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <p
                    className={`${
                      fieldState.error ? "text-red" : ""
                    } mb-[6px] text-sm`}
                  >
                    Navn
                  </p>
                  <FormControl>
                    <Input
                      placeholder="Skriv inn navn"
                      {...field}
                      className={`bg-white rounded-[8px] border text-black
                                  ${
                                    fieldState?.error
                                      ? "border-red"
                                      : "border-gray1"
                                  } `}
                      type="text"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`date`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <p
                    className={`${
                      fieldState.error ? "text-red" : ""
                    } mb-[6px] text-sm`}
                  >
                    Signert av kunde (dato)
                  </p>
                  <FormControl>
                    <Input
                      placeholder="Skriv inn dato"
                      {...field}
                      className={`bg-white rounded-[8px] border text-black
                                          ${
                                            fieldState?.error
                                              ? "border-red"
                                              : "border-gray1"
                                          } `}
                      type="date"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`pris`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <p
                    className={`${
                      fieldState.error ? "text-red" : ""
                    } mb-[6px] text-sm`}
                  >
                    Totalsum for{" "}
                    <span className="font-bold">{form.watch("navn")}</span>
                  </p>
                  <FormControl>
                    <Input
                      placeholder="Skriv inn pris"
                      {...field}
                      className={`bg-white rounded-[8px] border text-black
                                  ${
                                    fieldState?.error
                                      ? "border-red"
                                      : "border-gray1"
                                  } `}
                      inputMode="numeric"
                      type="text"
                      onChange={({ target: { value } }: any) =>
                        field.onChange({
                          target: {
                            name: `pris`,
                            value: value.replace(/\D/g, "")
                              ? new Intl.NumberFormat("no-NO").format(
                                  Number(value.replace(/\D/g, ""))
                                )
                              : "",
                          },
                        })
                      }
                      value={field.value === null ? "-" : field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex flex-col gap-3 md:gap-5">
              <FormField
                control={form.control}
                name="pdf"
                render={({ fieldState }) => (
                  <FormItem className="w-full">
                    <p
                      className={`${
                        fieldState.error ? "text-red" : "text-black"
                      } mb-[6px] text-sm font-medium`}
                    >
                      Bilder fra jobben:
                    </p>
                    <FormControl>
                      <div className="relative w-full p-2 rounded-lg bg-skyBlue">
                        <div className="bg-white p-1">
                          <div
                            className="flex gap-4 items-center p-2 justify-center border-gray3 border rounded-lg shadow-shadow5 h-[120px]"
                            onDragOver={handlepdfDragOver}
                            onClick={handlepdfClick}
                            onDrop={handlepdfDrop}
                          >
                            <div className="bg-primary h-12 w-12 justify-center items-center flex rounded-full">
                              <Upload className="text-white" />
                            </div>
                            <div className="flex flex-col gap-1.5">
                              <div className="text-black3 font-semibold text-xs md:text-sm">
                                Last opp dokumenter
                              </div>
                              <p className="text-gray4 text-xs md:text-sm">
                                Slipp fil for å laste opp her
                              </p>
                            </div>

                            <input
                              type="file"
                              ref={filepdfInputRef}
                              className="hidden"
                              accept=".pdf"
                              onChange={handlepdfFileChange}
                              name="pdf"
                            />
                          </div>
                        </div>
                        <p className="text-gray4 text-xs md:text-sm text-center w-full mt-3">
                          Filformater: kun{" "}
                          <span className="text-black3">
                            PDF (maks. 800x400px)
                          </span>
                        </p>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div>
                {uploadpdf && (
                  <div className="flex items-center gap-3 md:gap-5 flex-wrap">
                    {uploadpdf?.map((_file: any, index: number) => (
                      <div
                        className="relative h-[130px] md:h-[140px] w-[130px] md:w-[140px]"
                        key={index}
                      >
                        <img
                          src={Img_pdf}
                          alt="logo"
                          className="object-cover w-full h-full rounded-lg"
                        />
                        <div
                          className="absolute top-2 right-2 bg-grayOpacity rounded-[12px] p-[6px] cursor-pointer"
                          onClick={() => {
                            const updatedFiles = uploadpdf.filter(
                              (_: any, i: number) => i !== index
                            );
                            form.setValue("pdf", updatedFiles);
                          }}
                        >
                          <img src={Ic_delete_green} alt="delete" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end w-full gap-5 items-center px-5 pb-10 left-0">
            <div onClick={() => setEndringsmelding(false)}>
              <Button
                text="Tilbake"
                className="border border-gray2 text-black text-sm rounded-[8px] h-[40px] font-medium relative px-4 py-[10px] flex items-center gap-2"
              />
            </div>
            <div id="submit">
              <Button
                text="Neste"
                className="border border-primary bg-primary text-white text-sm rounded-[8px] h-[40px] font-medium relative px-4 py-[10px] flex items-center gap-2"
                type="submit"
              />
            </div>
          </div>
        </form>
      </Form>
    </>
  );
};
